/**
 * The API client — TECHNICAL_DESIGN.md §10.1, §3.1.
 *
 * `credentials: 'include'` and nothing else. The session is an HttpOnly, Secure,
 * SameSite=Strict cookie, so this file deliberately never reads, stores or
 * attaches a token: there is nothing for injected script to steal, and
 * SameSite=Strict is the CSRF defence rather than a second header we would have
 * to remember to send.
 *
 * Response shapes mirror `api/schemas.py`. Where the two could drift the type is
 * deliberately narrow — `precedence` and `origin` especially, since a component
 * that silently renders an unknown value is how an inherited rule loses its badge.
 */

export interface Rule {
  id: string
  rule_key: string
  layer: 'regulatory' | 'mandate' | 'house'
  template: string
  subject: Record<string, unknown>
  measure: string | null
  basis: string | null
  operator: string | null
  threshold: unknown
  evaluation_scope: string
  machine_checkable: boolean
  source_document_id: string | null
  source_clause: string | null
  /** What the clause is a clause *of*. `Sch. 2 para 4(b)` locates itself; a
   *  migrated rule cites a workbook cell, which locates nothing unless the
   *  reader is told which file it is a cell of, and can open it. */
  source_document_title: string | null
  source_document_kind: string | null
  /** A narrative rule carries no threshold — the sentence is the whole of it
   *  (§7.1), and a screen showing one without this shows a rule key and a label
   *  saying it is not a number. */
  narrative_text: string | null
  review_due_on: string | null
  /** §5.6: a limit shown against a portfolio nobody wrote it against must say so. */
  inherited: boolean
  origin: string
}

export interface Constraint {
  measure: string | null
  basis: string | null
  subject_key: string
  binding: Rule | null
  also: Rule[]
  /**
   * §6.3: restrictiveness depending on data release 1 does not have. Both rules
   * come back and neither is binding. The UI must not pick one.
   */
  undetermined: boolean
}

export interface Guidelines {
  portfolio_id: string
  as_of: string
  recorded_at: string
  constraints: Constraint[]
  /** Facts about the fund that are not limits, such as its benchmark. */
  facts?: { name: string; label: string; value: string; source_anchor: string | null }[]
}

export interface Principal {
  /** A property of the *appliance*, not the person, carried here because it has
   *  to reach every screen and this is the payload every screen already has.
   *  When true, inbound mail is admitted without its sender being established. */
  sender_authentication_relaxed?: boolean
  /** Add-on packs this installation runs, by code (decision 26). Carried on the
   *  principal because every role's screens speak of them. */
  add_ons?: string[]
  user_id: string
  email: string
  name: string
  /** The most senior role held, for places with room for one word. */
  role: string
  /** All of them. One person is frequently both author and approver. */
  roles: string[]
  /** The union of what those roles allow. Every screen gates on this. */
  capabilities: string[]
  /** The desks this person works on; empty is the whole firm. */
  desks?: string[]
}

/** One recorded act. `subject` is the thing's own name — a rule key, a fund
 *  code, an email — joined in by the store, because a trail saying *user
 *  6f85c28d changed rule a91f…* answers nothing the reader came with. */
export interface AuditEntry {
  id: number
  occurred_at: string
  action: string
  actor_email: string | null
  actor_name: string | null
  actor_type: string
  entity_type: string
  entity_id: string | null
  subject: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  request_id: string | null
  ip: string | null
}

/** A page of the trail. `next_before_id` is the cursor for the page after this
 *  one and is null on the last, so the screen never has to work out whether its
 *  own button does anything. There is no total: a count over a table that only
 *  grows is wrong by the time it renders. */
export interface AuditPage {
  entries: AuditEntry[]
  has_more: boolean
  next_before_id: number | null
}

/** One queue with something in it, and the screen that works it. `screen` is the
 *  application's own screen id: the nav owns routing, and a store handing back
 *  URLs would be a second router that only breaks when a tab is renamed. */
export interface PendingAction {
  key: string
  label: string
  count: number
  screen: string
  blurb: string
}

export interface PortfolioHit {
  id: string
  code: string
  name: string
  score: number
}

export interface RuleHit extends Rule {
  status: string
  rank: number
  /** Which fund. A firm-wide search whose results cannot say what they are
   *  attached to answers half the question. Null on a scope-attached rule,
   *  where `origin` carries the group instead. */
  portfolio_code: string | null
}

export interface Version {
  rule_id: string
  rule_key: string
  status: string
  layer: string
  template: string
  /** Which holdings this version covers. */
  subject?: Record<string, unknown> | null
  measure: string | null
  basis: string | null
  operator: string | null
  threshold: unknown
  effective_from: string
  /** Null on the version in force. A closed range is history and still answers
   *  for a date inside it (§5.2). */
  effective_to: string | null
  recorded_from: string
  recorded_to: string | null
  supersedes_id: string | null
  approver_user_id: string | null
  source_clause: string | null
  /** What the wording *was*, so a narrative rule's history shows more than that
   *  there was some. */
  narrative_text: string | null
  inherited: boolean
  origin: string | null
}

export interface HistoryEvent {
  audit_event_id: number
  occurred_at: string
  action: string
  actor_user_id: string | null
  actor_type: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}

export interface History {
  rule_key: string
  versions: Version[]
  events: HistoryEvent[]
}

/** A limit proposed for retirement, waiting for an approver (decision 72). */
export interface Retirement {
  id: string
  rule_id: string
  rule_key: string
  ends_on: string
  reason: string
  proposed_at: string
  proposed_by_user_id: string
  proposed_by: string
  template: string
  measure: string | null
  basis: string | null
  operator: string | null
  threshold: unknown
  subject: Record<string, unknown> | null
  narrative_text: string | null
  layer: 'regulatory' | 'mandate' | 'house'
  effective_from: string
  portfolio_code: string | null
  scope_code: string | null
  funds_affected: number
}

export interface PendingRule {
  rule_id: string
  rule_key: string
  layer: string
  template: string
  measure: string | null
  basis: string | null
  operator: string | null
  threshold: unknown
  effective_from: string
  channel: string | null
  portfolio_code: string | null
  scope_id: string | null
  scope_code: string | null
  supersedes_id: string | null
  source_document_id: string | null
  source_clause: string | null
  /** Who proposed it. Nobody approves their own proposal, so the queue marks those. */
  proposed_by_user_id?: string | null
  proposed_by?: string | null
  /** What kind of document it cites, not only that it cites one. A migrated
   *  rule cites its guideline pack — the client's own spreadsheet — which
   *  satisfies §5.5 and is not the contract. */
  source_document_kind: string | null
  source_document_title: string | null
  /** The workbook it was read out of, and where in it. Outlives the citation
   *  being upgraded to a mandate document at clause review. */
  origin_document_id: string | null
  origin_document_title: string | null
  source_anchor: string | null
  /** The whole content of a narrative rule (§7.1) — it carries no threshold. */
  narrative_text: string | null
  /** Null unless the proposal attaches to a scope (§5.6). */
  affected_portfolio_count: number | null
  /** What it covers, and what the version it supersedes covered. */
  subject?: Record<string, unknown> | null
  prior_subject?: Record<string, unknown> | null
}

/** A classification a subject can name, with its values today. */
export interface SchemeValues {
  code: string
  name: string
  members: { code: string; name: string }[]
}

/** The uploaded workbook, on its way to becoming a guideline pack. */
export interface PackRef {
  stored_uri: string
  content_hash: string
  filename: string
}

/** One limit standing identically on several funds, already in force. */
export interface Promotable {
  template: string
  layer: string
  measure: string | null
  basis: string | null
  subject: Record<string, unknown>
  evaluation_scope: string
  operator: string | null
  threshold: unknown
  portfolio_codes: string[]
  /** The ids too: the screen has to say *which* limits a promotion would
   *  duplicate, because promoting proposes another rule and closes none. */
  rule_ids: string[]
  rule_keys: string[]
  /** The desks those funds are on. */
  desk_codes?: string[]
  /** Each desk's share of those funds. */
  funds_by_desk?: Record<string, string[]>
}

export interface BlastRadius {
  scope_id: string
  portfolio_ids: string[]
  portfolio_codes: string[]
}

/** One recorded sign-off (§9.4, §9.6). */
export interface Attestation {
  id: string
  portfolio_id: string
  attested_by_user_id: string
  attested_at: string
  as_of: string
  recorded_at: string
  rule_count: number
  scope_count: number
  /** The reconstruction key is `(as_of, recorded_at)`; this proves the
   *  reconstruction is the same set. */
  rule_set_digest: string
  by_owner: boolean
  note: string | null
}

/** Whether a recorded sign-off still resolves to the set it was made over. */
export interface Reconstruction {
  attestation_id: string
  matches: boolean
  rule_count: number
}

/** A fund waiting for its sign-off (§9.4). */
export interface AttestationDue {
  portfolio_id: string
  code: string
  name: string
  owner_user_id: string | null
  /** Null on a fund never signed off and carrying no date — in the queue
   *  because it holds limits nobody has attested. */
  due_on: string | null
  days_overdue: number
  rule_count: number
  /** Null on a fund nobody has ever attested, which is a different fact from
   *  one whose attestation has lapsed. */
  last_attested_at: string | null
}

export interface ReviewItem {
  rule_id: string
  rule_key: string
  layer: string
  measure: string | null
  operator: string | null
  threshold: unknown
  review_due_on: string
  days_overdue: number
  source_document_id: string | null
  source_document_title: string | null
  source_version_label: string | null
  source_clause: string | null
  document_status: string | null
  portfolio_id?: string | null
  scope_id: string | null
  scope_code: string | null
  affected_portfolio_count: number
  drift_kind: string | null
}

export interface DictionaryKind {
  name: string
  label: string
  key: string[]
  fields: string[]
  required: string[]
  near_duplicate_checked: boolean
}

export interface NearDuplicate {
  code: string
  score: number
}

export interface PendingEntry {
  entry_id: string
  kind: string
  label: string
  values: Record<string, string | null>
  source: string
  near_duplicates: NearDuplicate[]
}

export interface SourceDocument {
  id: string
  title: string
  kind: string
  dms_uri: string
  version_label: string | null
  status: 'current' | 'superseded' | 'unresolved'
  effective_from: string | null
  content_hash: string | null
  rules_citing: number
}

export interface Scope {
  id: string
  type: string
  code: string
  name: string
  portfolio_count: number
  /** Whose group it is: a desk's, or the whole firm's when empty. */
  desk_id?: string | null
  desk_code?: string | null
  desk_name?: string | null
  /** The desks of the funds in it now. */
  member_desk_ids?: string[]
}

export interface MembershipPreview {
  portfolio_id: string
  scope_id: string
  scope: string
  change: 'join' | 'leave'
  rules: Rule[]
  rule_ids: string[]
}

export interface Finding {
  id: string
  rule_id: string
  rule_key: string
  portfolio_code: string | null
  kind: string
  source_document_id: string
  source_document_title: string | null
  source_clause: string | null
  /** Both, so the finding stays legible after the candidate is corrected. */
  workbook_value: unknown
  document_value: unknown
  note: string
  raised_at: string
}

export interface MigrationDraft {
  ref: string
  row_label: string
  portfolio_code: string
  /** The cell verbatim: a narrative draft *is* its text. */
  raw: string
  rule_key: string
  template: string
  measure: string | null
  operator: string | null
  threshold: unknown
  structured: boolean
}

export interface PromotionCandidate {
  template: string
  measure: string | null
  basis: string | null
  operator: string | null
  threshold: unknown
  /** What it constrains. Carried so promoting can write a complete rule rather
   *  than make somebody re-derive it from the workbook. */
  subject: Record<string, unknown>
  evaluation_scope: string
  portfolio_codes: string[]
  refs: string[]
}

export interface ParseResult {
  drafts: MigrationDraft[]
  attributes: { ref: string; portfolio_code: string; name: string; value: string }[]
  unmapped_rows: string[]
  structured_count: number
  narrative_count: number
  promotion_candidates: PromotionCandidate[]
  /** Rows whose share of NAV still covers the whole fund. */
  uncovered_rows?: string[]
}

export interface Divergence {
  ref: string
  row_label: string
  portfolio_code: string
  /** Null where an operator skipped the row — which is what stage 2 most likely
   *  catches, so it is a divergence rather than agreement. */
  first: { operator: string | null; threshold: unknown } | null
  second: { operator: string | null; threshold: unknown } | null
}

export interface DetectedShape {
  header_row: number
  label_column: number
  portfolio_codes: string[]
  /** How many header cells matched a portfolio already in the store — the number
   *  the operator judges the detection by. */
  matched: number
  /** Header columns this appliance has no fund for. Reported rather than
   *  created — a header typo and an unknown fund look identical to a machine,
   *  and this is the point at which a person can still tell them apart. */
  unmatched_portfolio_codes: string[]
  confident: boolean
  reason: string
}

export interface Upload {
  sheets: string[]
  sheet: string
  fingerprint: string
  known_profile: { id: string; name: string; row_map: Record<string, unknown> } | null
  shape: DetectedShape
  /** Re-cut into the grid the parser expects, so nothing is uploaded twice. */
  rows: string[][]
  preview: string[][]
  /** Where those rows and columns sit in the real sheet — see `Coordinates`. */
  column_refs: number[]
  sheet_offset: number
  /** The file itself, kept on the uploads volume. Handed back at staging so
   *  every rule out of this workbook cites it. Null where the volume could not
   *  be written, which degrades the import rather than failing it. */
  pack: PackRef | null
}

/**
 * Where a re-cut grid came from in the operator's own workbook.
 *
 * Passed back with every parse, reconcile and stage call. §7.1 keeps a cell
 * reference on each draft "so a reviewer can go and look at it", and on a sheet
 * with a spacer column or a header below the first row, a reference computed
 * from the re-cut points at the wrong cell.
 */
export interface Coordinates {
  column_refs: number[]
  sheet_offset: number
}

/** One entry in §8.5's queue: an instruction that arrived and was not acted on. */
export interface RefusedSubmission {
  id: string
  received_at: string
  /** When the appliance read it. Equal to `received_at` on a live poll; weeks
   *  later on a document that waited for its layout to be mapped. */
  read_at: string
  channel: string
  sender_address: string
  subject: string | null
  filename: string | null
  /** Which check refused it — sorts the queue. */
  refusal_kind: string
  /** The sentence that caused it — what somebody acts on. */
  refusal_reason: string | null
  stored_uri: string | null
  fingerprint: string | null
  portfolio_code: string | null
}

/** One *shape* nobody has mapped (§8.3). Grouped, because the work is per shape. */
export interface LayoutProfile {
  id: string
  name: string
  fingerprint: string
  sheet_name: string | null
  header_row: number
  label_column: number
  mapped_rows: number
  /** Where a document in this shape gets its effective date. A layout still
   *  reading `cell` with no cell named is one nobody has answered for, and it
   *  refuses everything that arrives in it — which is why the list shows it. */
  effective_date_cell: string | null
  effective_date_source: DateSource
  /** The newest document kept in this layout, which its row meanings are edited
   *  against. Null for a layout no document has arrived in by mail. */
  document_submission_id?: string | null
}

export interface DetectedShape {
  sheets: string[]
  sheet: string
  fingerprint: string
  known_profile: {
    id: string
    name: string
    row_map: unknown
    effective_date_cell: string | null
    effective_date_source: DateSource
  } | null
  shape: {
    header_row: number
    label_column: number
    portfolio_codes: string[]
    matched: number
    unmatched_portfolio_codes: string[]
    confident: boolean
    reason: string
  }
  rows: string[][]
  column_refs: string[]
  sheet_offset: number
  preview: string[][]
  pack: unknown
}

/** Where a layout's documents get their effective date: a cell, the day each
 *  arrives, or a date a person gives for each document (decision 70). */
export type DateSource = 'cell' | 'arrival' | 'document'

/** A document in a layout that carries no date, waiting for the day it takes effect. */
export interface AwaitingDate {
  id: string
  fingerprint: string
  filename: string | null
  sender_address: string
  subject: string | null
  received_at: string
  layout: string | null
}

export interface ReplayResult {
  read: number
  by_status: Record<string, number>
  proposals: number
}

export interface AwaitingMapping {
  fingerprint: string
  submissions: number
  first_seen: string
  last_seen: string
  example_filename: string | null
  example_submission_id: string
  example_sender: string
}

/** §8.6's allowlist. Revoked entries appear, with their date. */
export interface Sender {
  id: string
  email: string
  expected_domain: string
  portfolio_code: string
  added_at: string
  revoked_at: string | null
}

/**
 * The guideline mailbox's settings — §8.2, §8.6.
 *
 * **There is no password field, and that is deliberate rather than an
 * omission.** The credential cannot be hashed, because the connector presents
 * it to an IMAP server, so what is enforced instead is that nothing reads it
 * back: `password_set` answers the only question a screen needs about it.
 *
 * `source` says where the live settings come from. An appliance configured by
 * environment variables (§11.2) reports `environment`, and saving here takes
 * over from a file somebody else may still be maintaining — which the screen
 * says rather than leaving to be discovered.
 */
export interface ConnectorSettings {
  source: 'database' | 'environment'
  host: string
  username: string
  password_set: boolean
  authserv_id: string | null
  mailbox: string
  port: number
  starttls: boolean
  insecure: boolean
  search: string
  enabled: boolean
  updated_at: string | null
  updated_by: string | null
}

/** One message as the connector sees it. No content — filenames only. */
export interface ConnectorProbe {
  sender_address: string
  subject: string | null
  dmarc_aligned: boolean
  /**
   * §8.6's setting, read off real mail. Offered for an administrator to
   * recognise and never chosen automatically: picking one would let a message
   * nominate its own author as the authority on whether it is genuine.
   */
  authserv_ids: string[]
  attachments: string[]
}

export interface ConnectorTest {
  reached: boolean
  detail: string | null
  mailbox: string
  authserv_id: string | null
  messages: ConnectorProbe[]
}

/**
 * Automatic loading's settings (decision 43). Which way it signs in, and never the
 * password or private key themselves.
 */
export interface FeedSettings {
  /** SFTP, or FTP over TLS. Plain FTP is not offered. */
  protocol: 'sftp' | 'ftps'
  host: string
  port: number
  username: string
  sign_in: 'password' | 'key'
  folder: string
  pattern: string
  move_to: string | null
  /** Where benchmark figures files are collected from, or null where they are not. */
  figures_folder: string | null
  figures_pattern: string
  enabled: boolean
  /** The server key an administrator trusted, as `ssh-keygen -lf` prints it. */
  host_key: string | null
  key_trusted_at: string | null
  key_trusted_by: string | null
  updated_at: string
  updated_by: string | null
  /** Why files are not collected, or null when they are. */
  state: string | null
}

export type FeedStatus = 'loaded' | 'duplicate' | 'refused' | 'awaiting_mapping' | 'retrying'

/** A connection test: the key the server presented, beside the trusted one. Trusts nothing. */
export interface FeedTest {
  reached: boolean
  detail: string | null
  presented_key: string | null
  trusted_key: string | null
  folder: string
  protocol?: 'sftp' | 'ftps'
  /** Over FTPS, what the server's certificate says. */
  certificate?: {
    fingerprint: string
    names: string[]
    issuer: string | null
    not_after: string | null
    valid_for_host: boolean
    problem: string | null
  } | null
  files: { name: string; size: number; modified: string; handled: FeedStatus | null }[]
  /** The benchmark figures folder, listed the same way, where one is named. */
  figures?: {
    folder: string
    detail: string | null
    files: { name: string; size: number; modified: string; handled: FeedStatus | null }[]
  } | null
}

/** A file the feed collected, and what it came to. */
export interface FeedFile {
  id: string
  name: string
  size: number
  modified: string
  status: FeedStatus
  /** The reasons, in words, where it did not load. */
  detail: string[]
  handled_at: string
  snapshots: { id: string; fund: string; as_of: string }[]
  /** The run that last read it, or is queued to read it again. */
  job_id?: number | null
  /** Holdings, or benchmark figures. */
  kind?: 'holdings' | 'benchmark_figures'
  /** The figures a benchmark figures file loaded. */
  figures?: { id: string; filename: string; rows: number; benchmarks: string[]; days: string[] } | null
}

/** What redeeming an invitation produced (§10.1). */
export interface Redemption {
  email: string
  role: string
}

/** One account on the administration screen (§10.1). No credential material. */
export interface User {
  id: string
  email: string
  name: string
  /** Several is ordinary — one person is often both author and approver. */
  roles: string[]
  status: 'invited' | 'active' | 'deactivated'
  federated: boolean
  invitation_outstanding: boolean
  last_login_at: string | null
  created_at: string
  /** Desk codes the account is limited to; empty is the whole firm. */
  desks?: string[]
}

/** A shipped starter profile and what seeding it would add (§5.3.3). */
export interface MarketProfile {
  name: string
  title: string
  description: string
  counts: Record<string, number>
}

/** What seeding did. `skipped` is reported, never summed away. */
export interface SeedResult {
  profile: string
  added: Record<string, number>
  skipped: Record<string, number>
  total_added: number
  total_skipped: number
}

/** §5.4. Both numbers, because either alone misleads. */
export interface AuditStatus {
  intact: boolean
  broken_days: string[]
  last_sealed_day: string | null
  events_awaiting_seal: number
}

/** One background job, as the System screen lists it (decision 42). */
export interface JobRun {
  id: number
  kind: string
  label: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  attempts: number
  enqueued_at: string
  started_at: string | null
  finished_at: string | null
  summary: string | null
  error: string | null
  requested_by: string | null
  /** The desks of the funds it worked on. */
  desks?: string[]
  /** What it made that can be opened. */
  produced?: JobProduct[]
}

/** Something a run made: a snapshot, verdicts, a client's document, proposals for approval. */
/** A benchmark a fund's guidelines name, which limits against the benchmark are judged on (decisions 54, 55). */
export interface Benchmark {
  id: string
  name: string
  /** The newest day figures are loaded for. */
  latest_figures: string | null
  days: number
  /** The funds whose guidelines name it. */
  funds: string[]
  created_at: string
}

export interface BenchmarkFile {
  id: string
  filename: string
  row_count: number
  benchmarks: string[]
  days: string[]
  loaded_at: string
  loaded_by: string | null
}

export interface BenchmarkOverview {
  benchmarks: Benchmark[]
  files: BenchmarkFile[]
}

/** Compliance's verdict on a limit: within the limit, in breach, or not decidable (decision 53). */
export type ComplianceAnswer = 'within' | 'breach' | 'not_evaluable'

/** Why compliance's verdict differs from one GUARD decided. */
export type OverrideCause = 'limit' | 'data' | 'reading' | 'accepted'

/** Where a verdict stands with compliance. */
export type ReviewOutcome =
  | 'agreed'
  | 'overridden'
  | 'decided_by_compliance'
  | 'read_by_a_person'
  | 'awaiting_review'
  | 'changed_since_review'

export interface ComplianceReview {
  answer: ComplianceAnswer
  cause: OverrideCause | null
  note: string | null
  /** GUARD's verdict when compliance reviewed it. */
  guard_state: VerdictState
  reviewed_by: string | null
  reviewed_at: string
}

/** A verdict's standing with compliance: the newest review of it that day, and those before. */
export interface VerdictCompliance {
  outcome: ReviewOutcome
  review: ComplianceReview | null
  history: ComplianceReview[]
}

/** The figure shown beside verdicts. */
export interface AgreementHeadline {
  agreed: number
  overridden: number
  decided: number
  compared: number
  ratio: number
  reviewed: number
  awaiting: number
  funds: number
  funds_reviewed: number
}

export interface Agreement {
  headline: AgreementHeadline | null
  counts: Record<ReviewOutcome, number>
  gate: { funds: number; reviewed: number; met: boolean }
  causes: Partial<Record<OverrideCause, number>>
  /** The same measure desk by desk. */
  desks?: {
    desk: string
    name: string
    funds: number
    agreed: number
    overridden: number
    decided: number
    awaiting: number
    ratio: number | null
    gate_met: boolean
  }[]
  funds: {
    portfolio_id: string
    code: string
    name: string
    standing_day: string | null
    standing_run_id: string | null
    verdicts: number
    /** Every verdict compliance reviewed, limits written as words included. */
    reviewed: number
    awaiting: number
    agreed: number
    overridden: number
    decided: number
    read_by_a_person: number
    ratio: number | null
    fully_reviewed_days: string[]
  }[]
  /** Every verdict compliance overrode, or decided where GUARD could not. */
  overrides: {
    portfolio_id: string
    fund: string
    as_of: string
    run_id: string
    rule_id: string
    rule_key: string
    limit: string
    template: string
    guard_state: VerdictState
    outcome: ReviewOutcome
    review: ComplianceReview
  }[]
  read_by_people: {
    template: string
    label: string
    total: number
    not_evaluable: number
    judged: { fund: string; rule_key: string; state: VerdictState }[]
  }[]
}

/** How the holdings, the Dictionary and the limits disagree about a code (decision 50). */
export type FindingKind = 'cited_unheld' | 'undefined' | 'defined_unheld'

export interface ClassificationFinding {
  kind: FindingKind
  scheme: string
  scheme_name: string
  code: string
  nearest: string[]
  /** Held, not in the Dictionary. */
  holdings?: number
  funds?: string[]
  examples?: string[]
  requested?: boolean
  /** Named by a limit, not held. */
  limits?: string[]
  defined?: boolean
  /** Entered by hand, not held. */
  name?: string
  source?: string
  decision: { note: string; decided_by: string | null; decided_at: string } | null
}

export interface Reconciliation {
  snapshots: { fund: string; as_of: string }[]
  open: ClassificationFinding[]
  dismissed: ClassificationFinding[]
  counts: Record<FindingKind, number>
  not_carried: { scheme: string; name: string }[]
}

/** What one candidate trade did to the holdings (decision 49). */
export interface WhatIfTradeLine {
  side: 'buy' | 'sell'
  security_id: string
  amount: number
  held_before: number
  held_after: number
  new: boolean
  /** What was given for a new security that the snapshot has no column for. */
  ignored: string[]
}

/** A limit judged after the trades, with what it was before them. */
export interface WhatIfVerdict extends VerdictRow {
  before: { state: VerdictState; value: number | string | null; headroom: number | null }
  changed: boolean
}

export interface WhatIfResult {
  portfolio_id: string
  portfolio_code: string
  portfolio_name: string
  snapshot_id: string
  snapshot_as_of: string
  base_currency: string
  rules_recorded_at: string
  trades: WhatIfTradeLine[]
  cash: { security_id: string; before: number; after: number } | null
  counts_before: Record<VerdictState, number>
  counts_after: Record<VerdictState, number>
  text_only: number
  changed: number
  verdicts: WhatIfVerdict[]
  warn_tolerance: number
  notch_tolerance: number
}

/** What a what-if on a fund starts from. */
export interface WhatIfBasis {
  snapshot: SnapshotSummary | null
  holdings: { security_id: string; issuer_id: string | null; market_value: number; pct_of_nav: number | null }[]
  /** What a security the fund does not hold can be described by: the snapshot's columns. */
  fields: { column: string; label: string; kind: 'text' | 'number' | 'date' | 'boolean' }[]
  cash_line: string | null
}

/** Trades read from a sheet, each fund's result or why it could not be run. */
export interface WhatIfTableResult {
  columns: Record<string, string>
  funds: ((WhatIfResult & { fund: string; problems: [] }) | { fund: string; problems: string[] })[]
}

/** A cure obligation's state — TECHNICAL_DESIGN.md §9.5. */
export type CureState = 'open' | 'escalating' | 'closed_remediated' | 'closed_waived' | 'breached'

export interface CureObligation {
  id: string
  portfolio_id: string
  portfolio_code: string
  portfolio_name: string
  rule_key: string
  rule_id: string | null
  security_id: string
  issuer_id: string | null
  limit_text: string | null
  event: 'downgrade' | 'manual'
  /** The day the holding fell below, or the event a person recorded. */
  event_on: string
  period: string
  period_words: string
  calendar_code: string | null
  calendar_name: string | null
  deadline: string
  state: CureState
  escalation_level: number
  /** Calendar days to the deadline; null once it has ended. */
  days_left: number | null
  owner: string | null
  opened_at: string
  opened_by: string | null
  opened_by_run_id: string | null
  last_below_on: string | null
  note: string | null
  closed_at: string | null
  closed_by: string | null
  remediation: string | null
  remediation_run_id: string | null
  waiver_document_id: string | null
  waiver_document_title: string | null
  waiver_document_version: string | null
}

export interface CureHistoryEntry {
  occurred_at: string
  action: string
  actor_type: string
  actor: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}

export interface CureObligationDetail extends CureObligation {
  history: CureHistoryEntry[]
}

export interface CureListing {
  obligations: CureObligation[]
  totals: Record<CureState, number>
  today: string
}

export interface CureSettings {
  escalate_days: number[]
  updated_at: string
  updated_by: string | null
}

export interface SettlementCalendar {
  code: string
  name: string
  /** ISO weekday numbers, Monday 1 to Sunday 7. */
  weekend: number[]
  is_default: boolean
  created_at: string
  created_by: string | null
  holidays: number
  next_holiday: string | null
  last_holiday: string | null
  funds: string[]
}

export interface CalendarFund {
  id: string
  code: string
  name: string
  /** The fund's own calendar, or null for the firm's default. */
  calendar_code: string | null
  /** The calendar its deadlines fall on. */
  uses: string | null
}

export interface Holiday {
  day: string
  name: string | null
  recorded_at: string
  recorded_by: string | null
}

export interface JobProduct {
  type: 'snapshot' | 'run' | 'document' | 'proposals' | 'obligation'
  id: string
  label: string
  note: string | null
  /** The fund it belongs to, where it belongs to one. */
  fund?: string | null
  /** What it is, without the fund's name. */
  what?: string
  /** The desk of that fund. */
  desk?: string | null
}

/** A scheduled job as it stands here: when it runs, whether it is on, and its last run. */
export interface JobScheduleEntry {
  kind: string
  label: string
  cadence: string
  /** HH:MM UTC, for a job that runs at a time of day. */
  daily_at: string | null
  /** For a job that runs every so many minutes, with the intervals it may take. */
  every_minutes: number | null
  intervals: number[] | null
  /** When it runs unless an administrator changed it. */
  usual: string
  enabled: boolean
  offered: boolean
  why_not: string | null
  next_due: string | null
  changed_by: string | null
  changed_at: string | null
  last: JobRun | null
}

export interface JobsStatus {
  scheduler: { held: boolean; holder: string | null; renewed_at: string | null }
  schedule: JobScheduleEntry[]
}

export interface JobHistory {
  runs: JobRun[]
  total: number
  /** The jobs there are to filter by. */
  jobs: { kind: string; label: string }[]
}

export interface JobRunDetail extends JobRun {
  max_attempts: number
  run_after: string
  worker: string | null
  payload: Record<string, unknown>
  result: Record<string, unknown> | null
  produced: JobProduct[]
  /** A pass of automatic loading: the files it handled, as they stand now. */
  files: (FeedFile & {
    /** The run that has read it again since, or is queued to. */
    later_run: number | null
    /** What that later read came to; `retrying` while it waits. */
    later_status: FeedStatus | null
  })[] | null
}

export interface DriftPreview {
  documents_with_a_baseline: number
  resolver_configured: boolean
  detail: string
}

/** What this particular appliance lets an operator do. */
export interface OperationsCapabilities {
  factory_reset: boolean
  factory_reset_phrase: string
  /** Whether this appliance may switch §8.6's DMARC gate off at all. False on a
   *  customer installation, where the route is not registered either. */
  sender_authentication_switch: boolean
  sender_authentication_phrase: string
  /** Add-on packs and whether this installation runs each. Reported only —
   *  there is no call that changes one (IMPLEMENTATION.md decision 26). */
  add_ons: AddOn[]
}

/** How long one kind of record is kept (decision 35). */
export interface RetentionPolicy {
  category: string
  label: string
  covers: string
  years: number
  minimum: number
  default: number
  updated_at: string | null
  updated_by: string | null
}

export interface RetentionStatus {
  policy: RetentionPolicy[]
  kept: string[]
  preview: {
    today: string
    categories: {
      category: string
      before: string
      rows: number
      bytes: number
      snapshots?: number
      runs?: number
      verdicts?: number
      unsealed?: number
    }[]
  }
  usage: {
    database_bytes: number
    categories: Record<string, { bytes: number; rows: number; bytes_per_year: number }>
  }
  coefficients: {
    bytes: Record<'position' | 'snapshot' | 'run' | 'verdict' | 'audit_event', number>
    overhead: number
    business_days: number
    audit_events_per_user_per_day: number
  }
}

export interface AddOn {
  code: string
  name: string
  summary: string
  enabled: boolean
}

/** §13's round trip, in the half that is ours to run. */
export interface ExportVerdict {
  clean: boolean
  executable: number
  not_executable: number
  undetermined: number
  discrepancies: { portfolio_code: string; rule: string; detail: string }[]
}

/** The other half: what actually left the appliance, read back. */
export interface FileVerdict {
  faithful: boolean
  rules_read: number
  /** Null where the file carries no digest to check — only the JSON export has
   *  one. *Not checked* and *does not match* are opposite findings. */
  digest_matches: boolean | null
  /** Which reader ran. The two formats support different checks. */
  format: string
  discrepancies: { portfolio_code: string; rule: string; detail: string }[]
}

/** A fund this appliance holds (§5.1). */
export interface Portfolio {
  id: string
  code: string
  name: string
  base_currency: string
  domicile: string | null
  regime: string | null
  rule_count: number
  /** Who answers for the fund: signs it off and records its cure obligations. */
  owner_user_id?: string | null
  owner?: string | null
  owner_name?: string | null
  /** Something is recorded against the fund: its code and currency are fixed, and it stays. */
  in_use?: boolean
  /** The desk that runs the fund. */
  desk_id?: string
  desk_code?: string
  desk_name?: string
}

/** What this installation is licensed for (§12.4). A null limit is no limit. */
export interface LicenceStatus {
  state: 'none' | 'valid' | 'expired' | 'unverified'
  customer: string | null
  desks: number | null
  funds_per_desk: number | null
  packs: string[] | null
  issued: string | null
  expires: string | null
  problems: string[]
  desks_in_use: number
}

/** One of the firm's investment teams. Every fund is on exactly one. */
export interface Desk {
  id: string
  code: string
  name: string
  fund_count: number
  /** The starter markets it was set up with. */
  markets: string[]
}

/** Something a holdings column can be mapped to — §7.2, the Snapshot Evaluator pack. */
export interface HoldingsTarget {
  target: string
  label: string
  kind: 'text' | 'number' | 'date' | 'boolean'
  group: string
  required: boolean
}

/** The first date in a column that can only be read day first, or month first. */
export interface DateEvidence {
  day_first: string | null
  month_first: string | null
  /** Dates written like 03/04/2026, none of which decides it. */
  ambiguous: boolean
}

/** A holdings file read, with the mapping proposed for it. Nothing is loaded yet. */
export interface HoldingsReading {
  filename: string
  sheets: string[]
  sheet: string
  header_row: number
  confident: boolean
  reason: string
  headers: string[]
  preview: string[][]
  rows_read: number
  declared_count: number | null
  columns: Record<string, string>
  date_order: 'dmy' | 'mdy' | null
  /** Per column, what its dates prove about reading one written 03/04/2026. */
  date_evidence: Record<string, DateEvidence>
  /** The fund and the day the file's name gives, where it gives exactly one of each. */
  from_filename: { portfolio_id: string | null; portfolio_code: string | null; as_of: string | null }
  source: 'profile' | 'suggested'
  profile: { id: string; name: string } | null
  /** Columns a saved mapping never mapped, suggested for this file and saved with it. */
  added?: string[]
  targets: HoldingsTarget[]
}

/** The snapshot list's header filters and sort, applied by the server so they reach
 *  every snapshot and not only the first page. */
export interface SnapshotFilter {
  portfolioId?: string
  /** As-at days, `YYYY-MM-DD`, both included. */
  asOfFrom?: string
  asOfTo?: string
  /** Instants: the start of the reader's first day, and the end of their last. */
  loadedFrom?: string
  loadedBefore?: string
  sort?: string
  order?: 'asc' | 'desc'
  offset?: number
  limit?: number
  /** One desk's funds, by the desk's code. */
  desk?: string
}

/** A page of snapshots, and how many match in all. */
export interface SnapshotPage {
  snapshots: SnapshotSummary[]
  total: number
  offset: number
  limit: number
}

export interface SnapshotSummary {
  /** Its positions were removed under the retention policy; the header stays. */
  positions_removed?: boolean
  id: string
  portfolio_id: string
  portfolio_code: string
  portfolio_name: string
  as_of: string
  base_currency: string
  position_count: number
  declared_count: number | null
  total_market_value: number
  figures: Record<string, number>
  analytics: Record<string, number>
  columns: string[]
  notes: string[]
  filename: string
  loaded_at: string
  loaded_by: string | null
  profile_name: string | null
}

export interface SnapshotDetail extends SnapshotSummary {
  /** One page of positions, matching the search if there was one. */
  positions: Record<string, string | number | boolean | null>[]
  positions_total: number
  positions_offset: number
}

export type VerdictState = 'breach' | 'not_evaluable' | 'warn' | 'pass'

/** One snapshot judged against one fund's limits, recorded with its inputs (§6.2). */
export interface EvaluationRun {
  id: string
  portfolio_id: string
  portfolio_code: string
  portfolio_name: string
  snapshot_id: string
  snapshot_as_of: string
  snapshot_filename: string
  /** The two instants the limits were resolved at: the snapshot's date, and the run's. */
  rules_as_of: string
  rules_recorded_at: string
  warn_tolerance: number
  notch_tolerance: number
  engine_version: string
  rule_count: number
  counts: Record<VerdictState, number>
  policies_applied: string[]
  not_predicates: string[]
  inputs_digest: string
  results_digest: string
  ran_at: string
  run_by: string | null
  /** The newest run of this fund on this day, whichever of its snapshots it judged:
   *  the only run that is checked for reproducing. */
  latest: boolean
  /** Of the not-evaluable verdicts, the limits written as words: read by a person,
   *  never checked against holdings. */
  text_only?: number
}

/** What one rule came to in a run. */
export interface VerdictRow {
  rule_id: string
  rule_key: string
  template: string
  state: VerdictState
  value: number | string | null
  operator: string | null
  threshold: unknown
  unit: string | null
  headroom: number | null
  missing_inputs: string[]
  reason: string | null
  groups: {
    key: string | null
    state: VerdictState
    value: number | string | null
    low: number | null
    high: number | null
    headroom: number | null
    holdings: number
    /** Against the benchmark, the benchmark's own figure for this member. */
    benchmark?: number | null
  }[]
  holdings_considered: number
  layer: string
  subject: Record<string, unknown> | null
  measure: string | null
  basis: string | null
  origin: string
  /** A limit written as words is its wording, and where in the source it came from. */
  narrative_text?: string | null
  source_anchor?: string | null
  /** For a limit written as words: what the snapshot holds that its words name. */
  review?: {
    terms: { term: string; holdings: number; market_value: number; pct_of_nav: number | null }[]
    positions: {
      line: number
      security_id: string
      issuer_id: string | null
      market_value: number
      pct_of_nav: number | null
      terms: string[]
    }[]
    matched_holdings: number
  }
  /** Compliance's review of this verdict, where the Snapshot Evaluator pack runs (decision 53). */
  compliance?: VerdictCompliance
  /** Against the benchmark: the benchmark, its day, and its figure for the whole subject (decision 54). */
  benchmark?: VerdictBenchmark | null
}

export interface VerdictBenchmark {
  name: string
  as_of: string | null
  /** Null where each member is judged against its own weight. */
  value: number | null
  /** A composite's components: name, weight, day. */
  parts: [string, number, string][]
}

export interface EvaluationDetail extends EvaluationRun {
  verdicts: VerdictRow[]
  /** This run stands for its fund and day, so its verdicts are the ones compliance reviews. */
  reviewable?: boolean
  /** The fund's facts — benchmark, base currency, hedging — as recorded when the run was made. */
  facts?: { name: string; label: string; value: string; source_anchor: string | null }[]
  constraints: {
    measure: string | null
    basis: string | null
    subject_key: string
    state: VerdictState
    binding_rule_id: string | null
    also_rule_ids: string[]
  }[]
}

/** A run re-run from its own record, and whether it came out the same. */
export interface Reproduction {
  reproduces: boolean
  inputs_match: boolean
  differences: { rule_key: string; was: string | null; now: string | null }[]
  engine_version: string
  engine_version_now: string
}

/** Where a fund stands on its latest run, or that it has none. */
export type Standing = VerdictState | 'not_run'

/** One fund in the verdict report, with the run that stands for it. */
export interface VerdictReportFund {
  portfolio_id: string
  portfolio_code: string
  portfolio_name: string
  standing: Standing
  run: EvaluationRun | null
  /** The fund's limits for the run's day have been amended in the store since it ran. */
  limits_changed: boolean
  /** The benchmark figures for its day loaded since its run. */
  figures_changed?: boolean
  /** Holdings of the fund newer than the ones its standing run judged. */
  unevaluated_snapshot: { id: string; as_of: string } | null
}

export interface VerdictReport {
  on: string | null
  funds: VerdictReportFund[]
  totals: Record<Standing, number>
  /** How often verdicts agree with compliance's own answers, once measured. */
  agreement?: AgreementHeadline | null
}

export interface LoadSnapshotRequest {
  mapping: {
    sheet: string | null
    header_row: number
    columns: Record<string, string>
    date_order: string | null
  }
  as_of?: string
  portfolio_id?: string
  figures?: Record<string, string>
  save_profile_as?: string
}

/** One workbook row and what the system thinks it means, with the reason. */
export interface MappingSuggestion {
  label: string
  measure: string | null
  basis: string | null
  confidence: number
  why: string
  /** A rating or policy row, rather than a number. */
  template?: 'rating_floor' | 'average_rating_floor' | 'evaluation_policy' | null
  /** For a share of NAV, the holdings the row's name reads as covering. */
  subject?: Record<string, unknown> | null
  subject_why?: string | null
  /** A row that is a fact about the fund, such as its benchmark. */
  attribute?: string | null
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

/**
 * A failed response as an error somebody can read.
 *
 * The API returns `{detail}` for every mapped refusal, and those messages are
 * written for the person who hit them — *an approver has to do that*, *this
 * blast radius moved*. Replacing them with our own generic string would discard
 * the only part that says what to do next, so they are used verbatim.
 *
 * Two things this handles that the earlier `body.detail ?? response.statusText`
 * did not, and both of them ended with a screen doing nothing at all:
 *
 *   * **`statusText` is empty over HTTP/2.** The protocol dropped the reason
 *     phrase, and this appliance is served over TLS — so every unmapped failure,
 *     every 500, every proxy refusal produced `new ApiError(status, '')`. Every
 *     screen renders its error as `{error && <p>{error}</p>}`, an empty string
 *     is falsy, and the result was a click that visibly did nothing: no verdict,
 *     no error, no clue. A message is constructed from the status instead, which
 *     is never empty.
 *   * **FastAPI's 422 `detail` is a list, not a string.** Rendering it gave
 *     `[object Object]`, which is the same failure wearing a different coat.
 */
function refusal(status: number, detail: unknown): ApiError {
  if (typeof detail === 'string' && detail.trim()) {
    return new ApiError(status, detail)
  }

  // A validation refusal: FastAPI sends `[{loc, msg, type}, …]`.
  if (Array.isArray(detail)) {
    const said = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String(d.msg) : String(d)))
      .filter(Boolean)
      .join('; ')
    if (said) return new ApiError(status, said)
  }

  const said =
    status === 401
      ? 'Your session has ended. Sign in again.'
      : status === 403
        ? 'You are not allowed to do that.'
        : status === 404
          ? 'That is not here.'
          : status === 413
            ? 'That file is too large to send.'
            : status >= 500
              ? `The server could not complete that request (HTTP ${status}). Nothing was changed.`
              : `That request was refused (HTTP ${status}).`
  return new ApiError(status, said)
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { detail?: unknown }
    throw refusal(response.status, body.detail)
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

/** A refused file, with every problem the server found in it rather than the first. */
export class FileRefused extends ApiError {
  constructor(
    status: number,
    message: string,
    readonly problems: string[],
  ) {
    super(status, message)
  }
}

/** Candidate trades refused, with every trade that could not be applied (decision 49). */
export class TradesRefused extends ApiError {
  constructor(
    status: number,
    message: string,
    readonly problems: string[],
  ) {
    super(status, message)
  }
}

/** A JSON request whose refusal lists its problems, as a refused set of trades does. */
async function postListingProblems<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const found = (await response.json().catch(() => ({}))) as { detail?: unknown; problems?: unknown }
    const problems = Array.isArray(found.problems) ? found.problems.map(String) : []
    throw new TradesRefused(response.status, refusal(response.status, found.detail).message, problems)
  }
  return (await response.json()) as T
}

/** A multipart upload. No `content-type`: the browser sets the boundary, and
 *  `request` would overwrite it with JSON. */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(path, { method: 'POST', credentials: 'include', body: form })
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      detail?: unknown
      problems?: unknown
    }
    const problems = Array.isArray(body.problems) ? body.problems.map(String) : []
    throw new FileRefused(response.status, refusal(response.status, body.detail).message, problems)
  }
  return (await response.json()) as T
}

/**
 * A download rather than a JSON read.
 *
 * The export and the audit pack are artefacts somebody carries to another
 * system, so they arrive as files with filenames. Nothing is pushed anywhere —
 * §10.3 wants every outbound destination explicit and enumerable, and the way to
 * satisfy that is to have none.
 *
 * Returns the digest the appliance stamped on the response, which is the
 * receiving side's integrity check and belongs in front of the person who just
 * produced the file rather than only in a header they will not see.
 */
async function download(path: string, fallbackName: string): Promise<string | null> {
  const response = await fetch(path, { credentials: 'include' })
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { detail?: unknown }
    throw refusal(response.status, body.detail)
  }
  const disposition = response.headers.get('content-disposition') ?? ''
  const named = /filename="([^"]+)"/.exec(disposition)
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = named?.[1] ?? fallbackName
  link.click()
  URL.revokeObjectURL(url)
  return response.headers.get('x-anubandh-digest')
}

function reportQuery(filter: { on?: string; portfolioId?: string }, format?: string): string {
  const params = new URLSearchParams()
  if (filter.on) params.set('on', filter.on)
  if (filter.portfolioId) params.set('portfolio_id', filter.portfolioId)
  if (format) params.set('format', format)
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const api = {
  me: () => request<Principal>('/api/auth/me'),
  signIn: (email: string, password: string) =>
    request<Principal>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  signOut: () => request<void>('/api/auth/logout', { method: 'POST' }),

  guidelines: (portfolioId: string, asOf?: string) =>
    request<Guidelines>(
      `/api/portfolios/${portfolioId}/guidelines` + (asOf ? `?as_of=${asOf}` : ''),
    ),
  findPortfolios: (q: string) =>
    request<PortfolioHit[]>(`/api/search/portfolios?q=${encodeURIComponent(q)}`),
  /** Pending changes that would bind this portfolio (§8.6 — not limits). */
  proposals: (portfolioId: string) =>
    request<Rule[]>(`/api/portfolios/${portfolioId}/proposals`),
  history: (ruleId: string) => request<History>(`/api/rules/${ruleId}/history`),
  // Authoring and approval — §7.1, §8.6.
  pendingRules: () => request<PendingRule[]>('/api/rules/pending'),
  /** Identical active limits standing on several funds — what the scope layer
   *  exists to remove, and what nothing could find until now. */
  promotionCandidates: () =>
    request<Promotable[]>('/api/rules/promotion-candidates'),
  /** Limits recorded against the whole fund that need to say which holdings they cover. */
  blastRadius: (ruleId: string) =>
    request<BlastRadius>(`/api/rules/${ruleId}/blast-radius`),
  propose: (body: Record<string, unknown>) =>
    request<{ rule_id: string; rule_key: string; supersedes_id: string | null }>(
      '/api/rules',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  approve: (ruleId: string, shownPortfolioIds: string[] | null) =>
    request<{ rule_id: string; affected_portfolio_ids: string[] }>(
      `/api/rules/${ruleId}/approve`,
      { method: 'POST', body: JSON.stringify({ shown_portfolio_ids: shownPortfolioIds }) },
    ),
  /** End a limit without replacing it. Nothing is deleted — the effective range
   *  closes and the rule stays the correct answer for its own dates (§5.2). */
  /** Propose that a limit stops applying from a day (decision 72). It binds as
   *  before until an approver who did not propose it agrees. */
  proposeRetirement: (ruleId: string, endsOn: string, reason: string) =>
    request<{ id: string; rule_key: string; ends_on: string }>(
      `/api/rules/${ruleId}/retire`,
      { method: 'POST', body: JSON.stringify({ ends_on: endsOn, reason }) },
    ),
  retirements: () => request<Retirement[]>('/api/rules/retirements'),
  decideRetirement: (id: string, decision: 'approve' | 'decline', note?: string) =>
    request<{ id: string; rule_key: string; status: string }>(
      `/api/rules/retirements/${id}/${decision}`,
      { method: 'POST', body: JSON.stringify({ note: note || null }) },
    ),
  rejectRule: (ruleId: string, reason: string) =>
    request<void>(`/api/rules/${ruleId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * Upload a workbook and be told where its table appears to be (§7.1, §8.3).
   * No `content-type` header: the browser sets the multipart boundary, and
   * overriding it produces a body the server cannot parse.
   */
  migrationUpload: async (file: File): Promise<Upload> => {
    const form = new FormData()
    form.append('file', file)
    const response = await fetch('/api/migration/upload', {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { detail?: unknown }
      throw refusal(response.status, body.detail)
    }
    return (await response.json()) as Upload
  },

  // Workbook migration — §7.1 stages 1 and 2. Stateless: the rows travel with
  // every call, so `reconcile` genuinely re-parses both keyings.
  //
  // `where` travels with them, because the rows are a re-cut of the operator's
  // sheet. Without it every cell reference is computed against the re-cut, and a
  // reference exists so a reviewer can open the workbook and look at the cell.
  migrationParse: (
    rows: string[][],
    profile: Record<string, unknown>,
    where?: Coordinates,
  ) =>
    request<ParseResult>('/api/migration/parse', {
      method: 'POST',
      body: JSON.stringify({ rows, profile, ...(where ?? {}) }),
    }),
  migrationReconcile: (
    first: string[][],
    second: string[][],
    profile: Record<string, unknown>,
    where?: Coordinates,
  ) =>
    request<Divergence[]>('/api/migration/reconcile', {
      method: 'POST',
      body: JSON.stringify({ first, second, profile, ...(where ?? {}) }),
    }),
  migrationStage: (
    rows: string[][],
    profile: Record<string, unknown>,
    effectiveFrom: string,
    reconciledRefs: string[],
    where?: Coordinates,
    source: 'read' | 'keyed' = 'keyed',
    pack?: PackRef | null,
  ) =>
    request<{
      staged: number
      rule_ids: string[]
      /** Facts about a fund recorded, such as its benchmark. */
  facts_recorded?: number
  blocked_on_vocabulary: { ref: string; reason: string }[]
      unknown_portfolios: string[]
      pack_document_id: string | null
      /** Rows whose limit is already in force, unchanged — the ordinary result
       *  of re-importing a workbook with one column corrected. */
      unchanged: { ref: string; rule_key: string; row: string; portfolio_code: string }[]
      /** Rows whose limit already has the same proposal waiting for an approver. */
      already_proposed?: { ref: string; rule_key: string; row: string; portfolio_code: string }[]
      /** Rows colliding with an active rule this workbook is not a new version
       *  of. §7.1's question has an answer in the amending document. */
      needs_decision: { ref: string; rule_key: string; reason: string }[]
      /** Limits in force on these funds that the workbook no longer contains.
       *  Listed, not retired: that is proposed from Lookup (decision 72). */
      not_in_file?: { rule_id: string; rule_key: string; portfolio_code: string }[]
    }>('/api/migration/stage', {
      method: 'POST',
      body: JSON.stringify({
        rows,
        profile,
        effective_from: effectiveFrom,
        reconciled_refs: reconciledRefs,
        source,
        ...(pack ? { pack } : {}),
        ...(where ?? {}),
      }),
    }),

  // The guideline mailbox — §8.2, §8.3, §8.5, §8.6. No `accept`: a submission
  // becomes a live limit through the approvals screen and nowhere else, because
  // sender authorisation decides only whether it is *considered*.
  refusedSubmissions: () =>
    request<RefusedSubmission[]>('/api/submissions/refusals'),
  handleSubmission: (submissionId: string, note: string) =>
    request<void>(`/api/submissions/${submissionId}/handle`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  awaitingMapping: () =>
    request<AwaitingMapping[]>('/api/submissions/awaiting-mapping'),
  /** What a waiting document looks like — the same detection the import wizard
   *  runs, so the shape somebody confirms is the shape that gets read. */
  submissionShape: (submissionId: string) =>
    request<DetectedShape>(`/api/submissions/${submissionId}/shape`),
  /** Read everything that was waiting on a shape, now it is mapped. Separate
   *  from confirming the mapping: the mapping may be right and the backlog may
   *  still want looking at first. */
  replayShape: (fingerprint: string, effectiveOn?: string, submissionId?: string) =>
    request<ReplayResult>('/api/submissions/replay', {
      method: 'POST',
      body: JSON.stringify({
        fingerprint,
        ...(effectiveOn ? { effective_on: effectiveOn } : {}),
        ...(submissionId ? { submission_id: submissionId } : {}),
      }),
    }),
  awaitingDate: () => request<AwaitingDate[]>('/api/submissions/awaiting-date'),
  layoutProfiles: () =>
    request<LayoutProfile[]>('/api/migration/layout-profiles'),
  confirmLayout: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/migration/layout-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // The narrowest edit a mapped layout allows. A profile confirmed before the
  // effective date was asked for refuses every document in its shape, and the
  // shape has long since left the queue that was the only way back to the
  // mapper — so without this the client's guidelines stop for good.
  rebindEffectiveDate: (
    profileId: string,
    source: DateSource,
    cell: string | null,
  ) =>
    request<{ id: string }>(
      `/api/migration/layout-profiles/${profileId}/effective-date`,
      {
        method: 'POST',
        body: JSON.stringify({
          effective_date_source: source,
          effective_date_cell: source === 'cell' ? cell : null,
        }),
      },
    ),
  // Every queue the signed-in principal can act on, with how much is in each.
  // Gated per row in the store on what they hold, so this asks for no capability
  // of its own — a permission somebody must be granted before the product will
  // tell them their own workload is one more thing to forget on a new role.
  pendingActions: () => request<PendingAction[]>('/api/pending-actions'),

  // The audit trail — §5.4. Paged by cursor rather than by page number: the
  // table grows at the newest end, so an offset means different rows each time
  // it is used and a reader would see entries twice and miss others.
  auditTrail: (params: {
    action?: string
    entityType?: string
    actorUserId?: string
    since?: string
    until?: string
    beforeId?: number
    limit?: number
  }) => {
    const q = new URLSearchParams()
    if (params.action) q.set('action', params.action)
    if (params.entityType) q.set('entity_type', params.entityType)
    if (params.actorUserId) q.set('actor_user_id', params.actorUserId)
    // A date on its own is midnight local; the store compares instants. `until`
    // is exclusive, so the end of a day is the start of the next one — a
    // half-open range is the only kind that cannot double-count a boundary.
    if (params.since) q.set('since', `${params.since}T00:00:00Z`)
    if (params.until) q.set('until', `${params.until}T00:00:00Z`)
    if (params.beforeId) q.set('before_id', String(params.beforeId))
    if (params.limit) q.set('limit', String(params.limit))
    return request<AuditPage>(`/api/audit/trail?${q}`)
  },
  auditActors: () => request<{ id: string; email: string }[]>('/api/audit/actors'),
  auditActions: () =>
    request<{ action: string; count: number }[]>('/api/audit/actions'),
  senders: () => request<Sender[]>('/api/senders'),
  authoriseSender: (email: string, expectedDomain: string, portfolioId: string) =>
    request<Sender>('/api/senders', {
      method: 'POST',
      body: JSON.stringify({
        email,
        expected_domain: expectedDomain,
        portfolio_id: portfolioId,
      }),
    }),
  // A POST, not a DELETE: nothing is removed. The row stops being effective and
  // stays queryable, because an instruction accepted last year was accepted
  // under an authorisation that has to remain explicable (§8.6).
  revokeSender: (authorisationId: string) =>
    request<void>(`/api/senders/${authorisationId}/revoke`, { method: 'POST' }),

  // The clause review — §7.1.
  clauseFindings: () => request<Finding[]>('/api/clause-findings'),
  cite: (ruleId: string, sourceDocumentId: string, sourceClause: string) =>
    request<void>(`/api/rules/${ruleId}/cite`, {
      method: 'POST',
      body: JSON.stringify({
        source_document_id: sourceDocumentId,
        source_clause: sourceClause,
      }),
    }),
  disagree: (ruleId: string, body: Record<string, unknown>) =>
    request<{ finding_id: string }>(`/api/rules/${ruleId}/disagree`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resolveFinding: (findingId: string, resolution: string, note: string) =>
    request<void>(`/api/clause-findings/${findingId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution, note }),
    }),

  // Review and attestation — §9.2, §9.4.
  reviews: (mine = true, withinDays = 0) =>
    request<ReviewItem[]>(`/api/reviews?mine=${mine}&within_days=${withinDays}`),
  confirmReview: (ruleId: string, checkedDocumentId: string, note?: string) =>
    request<{ next_review_due_on: string }>(`/api/reviews/${ruleId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ checked_document_id: checkedDocumentId, note: note ?? null }),
    }),
  /** Which funds are waiting for a sign-off (§9.4). The date has been tracked
   *  since the membership control shipped and nothing ever showed it. */
  attestationsDue: (withinDays = 0) =>
    request<AttestationDue[]>(`/api/attestations/due?within_days=${withinDays}`),
  attestationHistory: (portfolioId: string) =>
    request<Attestation[]>(`/api/portfolios/${portfolioId}/attestation`),
  /** Re-resolve what was signed off and say whether it still matches. Real work
   *  — the whole resolution re-runs at the recorded instants — so it is asked
   *  for per row rather than run down a page. */
  attestationReconstruction: (attestationId: string) =>
    request<Reconstruction>(`/api/attestations/${attestationId}/reconstruction`),
  attestationBlockers: (portfolioId: string) =>
    request<ReviewItem[]>(`/api/portfolios/${portfolioId}/attestation/blockers`),
  attest: (portfolioId: string, note: string) =>
    request<{ id: string; rule_count: number }>(
      `/api/portfolios/${portfolioId}/attestation`,
      {
        method: 'POST',
        body: JSON.stringify({
          accuracy_affirmed: true,
          completeness_affirmed: true,
          note: note || null,
        }),
      },
    ),

  // The market-definition surface — §5.3.3.
  dictionaryKinds: () => request<DictionaryKind[]>('/api/dictionary/kinds'),
  dictionary: (kind: string) => request<Record<string, string>[]>(`/api/dictionary/${kind}`),
  vocabularyQueue: () => request<PendingEntry[]>('/api/dictionary/requests'),
  requestEntry: (kind: string, values: Record<string, string>) =>
    request<{ entry_id: string; near_duplicates: NearDuplicate[] }>(
      `/api/dictionary/${kind}/requests`,
      { method: 'POST', body: JSON.stringify({ values }) },
    ),
  approveEntry: (kind: string, entryId: string, despiteSimilar: string[]) =>
    request<void>(`/api/dictionary/${kind}/requests/${entryId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ despite_similar: despiteSimilar }),
    }),
  rejectEntry: (kind: string, entryId: string, reason: string) =>
    request<void>(`/api/dictionary/${kind}/requests/${entryId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Rule export — §5.6, §13, launch gate 4. `approver` only.
  downloadRuleExport: (portfolioId: string | undefined, format: 'json' | 'csv', desk?: string) => {
    const params = new URLSearchParams({ format })
    if (portfolioId) params.set('portfolio_id', portfolioId)
    if (desk) params.set('desk', desk)
    return download(`/api/rule-export?${params}`, `anubandh-rules.${format}`)
  },
  verifyExport: (portfolioId?: string, desk?: string) => {
    const params = new URLSearchParams()
    if (portfolioId) params.set('portfolio_id', portfolioId)
    if (desk) params.set('desk', desk)
    const query = params.toString()
    return request<ExportVerdict>('/api/rule-export/verify' + (query ? `?${query}` : ''))
  },
  verifyExportFile: async (file: File) => {
    const body = new FormData()
    body.append('file', file)
    // No `content-type`: the browser has to set the multipart boundary, and
    // `request` would override it with JSON.
    const response = await fetch('/api/rule-export/verify-file', {
      method: 'POST',
      credentials: 'include',
      body,
    })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { detail?: unknown }
      throw refusal(response.status, body.detail)
    }
    return (await response.json()) as FileVerdict
  },
  downloadAuditPack: (portfolioId?: string) =>
    download(
      '/api/audit-pack' + (portfolioId ? `?portfolio_id=${portfolioId}` : ''),
      'anubandh-audit-pack.json',
    ),

  // Holdings snapshots — the Snapshot Evaluator pack (§7.2).
  readHoldings: (file: File, sheet?: string, headerRow?: number) => {
    const form = new FormData()
    form.append('file', file)
    if (sheet) form.append('sheet', sheet)
    if (headerRow !== undefined) form.append('header_row', String(headerRow))
    return upload<HoldingsReading>('/api/holdings/read', form)
  },
  loadSnapshot: (file: File, body: LoadSnapshotRequest) => {
    const form = new FormData()
    form.append('file', file)
    form.append('mapping', JSON.stringify(body.mapping))
    if (body.as_of) form.append('as_of', body.as_of)
    if (body.portfolio_id) form.append('portfolio_id', body.portfolio_id)
    if (body.figures && Object.keys(body.figures).length > 0) {
      form.append('figures', JSON.stringify(body.figures))
    }
    if (body.save_profile_as) form.append('save_profile_as', body.save_profile_as)
    return upload<{ snapshots: SnapshotSummary[] }>('/api/holdings/snapshots', form)
  },
  snapshots: (filter: SnapshotFilter = {}) => {
    const params = new URLSearchParams()
    if (filter.portfolioId) params.set('portfolio_id', filter.portfolioId)
    if (filter.asOfFrom) params.set('as_of_from', filter.asOfFrom)
    if (filter.asOfTo) params.set('as_of_to', filter.asOfTo)
    if (filter.loadedFrom) params.set('loaded_from', filter.loadedFrom)
    if (filter.loadedBefore) params.set('loaded_before', filter.loadedBefore)
    if (filter.sort) params.set('sort', filter.sort)
    if (filter.sort && filter.order) params.set('order', filter.order)
    if (filter.offset) params.set('offset', String(filter.offset))
    if (filter.limit) params.set('limit', String(filter.limit))
    if (filter.desk) params.set('desk', filter.desk)
    const query = params.toString()
    return request<SnapshotPage>('/api/holdings/snapshots' + (query ? `?${query}` : ''))
  },
  snapshot: (id: string, page: { offset?: number; limit?: number; find?: string } = {}) => {
    const params = new URLSearchParams()
    if (page.offset) params.set('offset', String(page.offset))
    if (page.limit) params.set('limit', String(page.limit))
    if (page.find) params.set('find', page.find)
    const query = params.toString()
    return request<SnapshotDetail>(`/api/holdings/snapshots/${id}${query ? `?${query}` : ''}`)
  },
  /** Every position of a snapshot as a file. */
  downloadSnapshot: (id: string, format: 'csv' | 'xlsx') =>
    download(`/api/holdings/snapshots/${id}/export?format=${format}`, `holdings.${format}`),

  // Evaluation runs — the Snapshot Evaluator pack (§6.2, proposal §5.5).
  evaluate: (snapshotId: string) =>
    request<EvaluationRun>('/api/evaluations', {
      method: 'POST',
      body: JSON.stringify({ snapshot_id: snapshotId }),
    }),
  evaluations: (filter: { snapshotId?: string; portfolioId?: string } = {}) => {
    const params = new URLSearchParams()
    if (filter.snapshotId) params.set('snapshot_id', filter.snapshotId)
    if (filter.portfolioId) params.set('portfolio_id', filter.portfolioId)
    const query = params.toString()
    return request<EvaluationRun[]>(`/api/evaluations${query ? `?${query}` : ''}`)
  },
  evaluation: (id: string) => request<EvaluationDetail>(`/api/evaluations/${id}`),
  reproduceEvaluation: (id: string) =>
    request<Reproduction>(`/api/evaluations/${id}/reproduce`, { method: 'POST' }),
  /** One run's verdicts as a file. */
  downloadRun: (id: string, format: 'csv' | 'xlsx') =>
    download(`/api/evaluations/${id}/export?format=${format}`, `verdicts.${format}`),
  /** Every fund with the run that stands for it, as at the end of a day or now. */
  verdictReport: (filter: { on?: string; portfolioId?: string } = {}) =>
    request<VerdictReport>(`/api/evaluations/report${reportQuery(filter)}`),
  downloadVerdictReport: (filter: { on?: string; portfolioId?: string }, format: 'csv' | 'xlsx') =>
    download(
      `/api/evaluations/report/export${reportQuery(filter, format)}`,
      `verdicts.${format}`,
    ),

  // Benchmarks and their figures — proposal §4.1, §6.4, decisions 54 and 55. The Snapshot Evaluator pack.
  benchmarks: () => request<BenchmarkOverview>('/api/benchmarks'),
  loadBenchmarkFigures: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return upload<{
      file: { id: string; filename: string; row_count: number }
      rows: number
      benchmarks: string[]
      days: string[]
    }>('/api/benchmarks/figures', form)
  },
  downloadBenchmarkTemplate: (format: 'csv' | 'xlsx') =>
    download(`/api/benchmarks/figures/template?format=${format}`, `benchmark_figures.${format}`),

  // Compliance agreement — proposal §6 second-release gate, decisions 52 and 53. The Snapshot Evaluator pack.
  agreement: () => request<Agreement>('/api/agreement'),
  reviewVerdict: (
    runId: string,
    ruleId: string,
    body: { answer: ComplianceAnswer; cause: OverrideCause | null; note: string },
  ) =>
    request<VerdictCompliance>('/api/agreement/reviews', {
      method: 'POST',
      body: JSON.stringify({ run_id: runId, rule_id: ruleId, ...body }),
    }),

  // Classification reconciliation — §5.3.2, decision 50. The Snapshot Evaluator pack.
  reconciliation: () => request<Reconciliation>('/api/reconciliation'),
  dismissFinding: (finding: Pick<ClassificationFinding, 'scheme' | 'code' | 'kind'>, note: string) =>
    request<Reconciliation>('/api/reconciliation/dismiss', {
      method: 'POST',
      body: JSON.stringify({ scheme: finding.scheme, code: finding.code, kind: finding.kind, note }),
    }),
  reopenFinding: (finding: Pick<ClassificationFinding, 'scheme' | 'code' | 'kind'>) =>
    request<Reconciliation>('/api/reconciliation/reopen', {
      method: 'POST',
      body: JSON.stringify({ scheme: finding.scheme, code: finding.code, kind: finding.kind }),
    }),
  requestMember: (scheme: string, code: string, name: string) =>
    request<{ entry_id: string; near_duplicates: NearDuplicate[] }>('/api/reconciliation/request-member', {
      method: 'POST',
      body: JSON.stringify({ scheme, code, name }),
    }),

  // Candidate trades — proposal §5.3, decision 49. The Snapshot Evaluator pack.
  whatIfBasis: (portfolioId: string) =>
    request<WhatIfBasis>(`/api/evaluations/what-if/basis?portfolio_id=${encodeURIComponent(portfolioId)}`),
  whatIf: (
    snapshotId: string,
    trades: { side: string; security_id: string; amount: number; attributes: Record<string, string> }[],
  ) => postListingProblems<WhatIfResult>('/api/evaluations/what-if', { snapshot_id: snapshotId, trades }),

  // Cure obligations and settlement calendars — §6.5, §9.5. The Snapshot Evaluator pack.
  cureObligations: () => request<CureListing>('/api/cure-obligations'),
  cureObligation: (id: string) => request<CureObligationDetail>(`/api/cure-obligations/${id}`),
  openCureObligation: (body: {
    portfolio_id: string
    rule_id: string
    security_id: string
    issuer_id: string | null
    event_on: string
    period: string | null
    note: string
  }) =>
    request<CureObligationDetail>('/api/cure-obligations', { method: 'POST', body: JSON.stringify(body) }),
  remediateCureObligation: (id: string, reference: string, note: string | null) =>
    request<CureObligationDetail>(`/api/cure-obligations/${id}/remediate`, {
      method: 'POST',
      body: JSON.stringify({ reference, note }),
    }),
  waiveCureObligation: (id: string, documentId: string, note: string | null) =>
    request<CureObligationDetail>(`/api/cure-obligations/${id}/waive`, {
      method: 'POST',
      body: JSON.stringify({ document_id: documentId, note }),
    }),
  cureSettings: () => request<CureSettings>('/api/cure-obligations/settings'),
  setCureEscalation: (days: number[]) =>
    request<CureSettings>('/api/cure-obligations/settings', {
      method: 'PUT',
      body: JSON.stringify({ escalate_days: days }),
    }),
  settlementCalendars: () =>
    request<{ calendars: SettlementCalendar[]; funds: CalendarFund[] }>('/api/settlement-calendars'),
  createSettlementCalendar: (body: { code: string; name: string; weekend: number[]; is_default: boolean }) =>
    request<SettlementCalendar>('/api/settlement-calendars', { method: 'POST', body: JSON.stringify(body) }),
  updateSettlementCalendar: (code: string, body: { name?: string; is_default?: boolean }) =>
    request<SettlementCalendar>(`/api/settlement-calendars/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  holidays: (code: string) =>
    request<Holiday[]>(`/api/settlement-calendars/${encodeURIComponent(code)}/holidays`),
  addHolidays: (code: string, text: string) =>
    request<{ added: number; already: number; deadlines_moved: number }>(
      `/api/settlement-calendars/${encodeURIComponent(code)}/holidays`,
      { method: 'POST', body: JSON.stringify({ text }) },
    ),
  removeHoliday: (code: string, day: string) =>
    request<void>(`/api/settlement-calendars/${encodeURIComponent(code)}/holidays/${day}/remove`, { method: 'POST' }),
  assignSettlementCalendar: (portfolioId: string, code: string | null) =>
    request<CalendarFund>(`/api/portfolios/${portfolioId}/settlement-calendar`, {
      method: 'PUT',
      body: JSON.stringify({ code }),
    }),

  // Running the appliance — §5.4, §9.2, §12.3. `admin` only.
  auditStatus: () => request<AuditStatus>('/api/operations/audit'),
  sealAudit: () =>
    request<{ sealed: boolean; day: string | null; events: number | null }>(
      '/api/operations/audit/seal',
      { method: 'POST' },
    ),
  driftPreview: () => request<DriftPreview>('/api/operations/drift'),
  jobs: () => request<JobsStatus>('/api/operations/jobs'),
  runJob: (kind: string) =>
    request<JobRun>(`/api/operations/jobs/${encodeURIComponent(kind)}/run`, { method: 'POST' }),
  /** Turn a job off or on, or change when it runs. Each field left out stays as it is. */
  setJobSchedule: (kind: string, body: { enabled?: boolean; daily_at?: string; every_minutes?: number }) =>
    request<JobScheduleEntry>(`/api/operations/jobs/${encodeURIComponent(kind)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  jobHistory: (
    filters: {
      kind?: string
      state?: string
      queuedFrom?: string
      queuedBefore?: string
      limit?: number
    } = {},
  ) => {
    const params = new URLSearchParams()
    if (filters.kind) params.set('kind', filters.kind)
    if (filters.state) params.set('state', filters.state)
    if (filters.queuedFrom) params.set('queued_from', filters.queuedFrom)
    if (filters.queuedBefore) params.set('queued_before', filters.queuedBefore)
    if (filters.limit) params.set('limit', String(filters.limit))
    const query = params.toString()
    return request<JobHistory>(`/api/jobs/runs${query ? `?${query}` : ''}`)
  },
  jobRun: (id: number) => request<JobRunDetail>(`/api/jobs/runs/${id}`),
  /** The document a client sent, as it arrived. */
  downloadSubmissionDocument: (id: string) => download(`/api/submissions/${id}/document`, 'submission'),
  operationsCapabilities: () =>
    request<OperationsCapabilities>('/api/operations/capabilities'),
  retention: () => request<RetentionStatus>('/api/operations/retention'),
  setRetention: (category: string, years: number) =>
    request<RetentionPolicy>(`/api/operations/retention/${category}`, {
      method: 'PUT',
      body: JSON.stringify({ years }),
    }),
  // Demonstration sandboxes only — the route does not exist elsewhere.
  /** Turn §8.6's sender authentication off, or back on. The phrase is required
   *  in one direction only — see `operations.accept_unaligned_senders`. */
  setSenderAuthentication: (off: boolean, confirmation = '') =>
    request<{ sender_authentication_off: boolean }>(
      '/api/operations/sender-authentication',
      { method: 'POST', body: JSON.stringify({ off, confirmation }) },
    ),
  factoryReset: (confirmation: string) =>
    request<{ admin_email: string; invitation_token: string }>(
      '/api/operations/factory-reset',
      { method: 'POST', body: JSON.stringify({ confirmation }) },
    ),
  diagnostics: () =>
    request<{ contents: string[]; bundle: Record<string, unknown> }>(
      '/api/operations/diagnostics',
      { method: 'POST' },
    ),

  // The starter vocabulary per market — §5.3.3.
  marketProfiles: () => request<MarketProfile[]>('/api/dictionary/profiles'),
  seedMarketProfile: (name: string) =>
    request<SeedResult>(`/api/dictionary/profiles/${name}`, { method: 'POST' }),

  // What each workbook row probably means — a read against your own dictionary.
  suggestMapping: (labels: string[]) =>
    request<MappingSuggestion[]>('/api/migration/suggest-mapping', {
      method: 'POST',
      body: JSON.stringify({ labels }),
    }),
  /** What a row's limit can cover, and the measures that have to say. */
  migrationSubjects: () =>
    request<{ schemes: SchemeValues[]; needs_subject: string[] }>('/api/migration/subjects'),

  // Portfolios — §5.1. Listing is a read; creating is `admin`.
  portfolios: () => request<Portfolio[]>('/api/portfolios'),
  /** Who answers for a fund, or nobody. An administrator's. */
  setFundOwner: (portfolioId: string, ownerUserId: string | null) =>
    request<Portfolio>(`/api/portfolios/${portfolioId}/owner`, {
      method: 'PUT',
      body: JSON.stringify({ owner_user_id: ownerUserId }),
    }),
  createPortfolio: (body: {
    code: string
    name: string
    base_currency: string
    domicile?: string | null
    regime?: string | null
    desk_id?: string | null
  }) => request<Portfolio>('/api/portfolios', { method: 'POST', body: JSON.stringify(body) }),
  moveFund: (portfolioId: string, deskId: string) =>
    request<Portfolio>(`/api/portfolios/${portfolioId}/desk`, {
      method: 'PUT',
      body: JSON.stringify({ desk_id: deskId }),
    }),

  // Desks — the firm's investment teams.
  desks: () => request<Desk[]>('/api/desks'),
  licence: () => request<LicenceStatus>('/api/licence'),
  /** Who may own a fund on this desk: authors and approvers who can see it. */
  deskOwners: (deskId: string) =>
    request<{ id: string; name: string; email: string }[]>(`/api/desks/${deskId}/owners`),
  createDesk: (body: { code: string; name: string }) =>
    request<Desk>('/api/desks', { method: 'POST', body: JSON.stringify(body) }),
  updateDesk: (deskId: string, body: { code: string; name: string }) =>
    request<Desk>(`/api/desks/${deskId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteDesk: (deskId: string) => request<void>(`/api/desks/${deskId}`, { method: 'DELETE' }),
  addDeskMarket: (deskId: string, market: string) =>
    request<SeedResult>(`/api/desks/${deskId}/markets/${market}`, { method: 'POST' }),
  /** Correct a fund's record. The code and currency only while nothing refers to the fund. */
  updatePortfolio: (
    portfolioId: string,
    body: { code: string; name: string; base_currency: string; domicile?: string | null; regime?: string | null },
  ) => request<Portfolio>(`/api/portfolios/${portfolioId}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** Remove a fund nothing was ever recorded against. Refused otherwise. */
  deletePortfolio: (portfolioId: string) =>
    request<void>(`/api/portfolios/${portfolioId}`, { method: 'DELETE' }),

  // Scopes and membership — §5.6, §10.1.
  scopes: () => request<Scope[]>('/api/scopes'),
  /** The funds a group currently reaches. A screen that lets somebody take one
   *  out has to show which are in — asking the reader to remember is asking
   *  them to guess, where the consequence is a limit ceasing to bind. */
  /** The limits a group carries. Reachable before only through the membership
   *  preview, which needs a fund to ask about — so the plainer question, *what
   *  does this group say*, had no answer. */
  scopeRules: (scopeId: string) => request<Rule[]>(`/api/scopes/${scopeId}/rules`),
  scopeMembers: (scopeId: string) =>
    request<{ portfolio_id: string; code: string; name: string }[]>(
      `/api/scopes/${scopeId}/members`,
    ),
  previewLeave: (scopeId: string, portfolioId: string) =>
    request<MembershipPreview>(
      `/api/scopes/${scopeId}/preview-leave?portfolio_id=${portfolioId}`,
    ),
  /** POST, and the verb is the honest one: nothing is removed. The membership
   *  closes in business time and succeeds in decision time, so the period the
   *  fund really was bound stays queryable (§5.2). */
  endMembership: (scopeId: string, portfolioId: string, shownRuleIds: string[]) =>
    request<{ attestation_due_on: string }>(
      `/api/scopes/${scopeId}/members/${portfolioId}/end`,
      { method: 'POST', body: JSON.stringify({ shown_rule_ids: shownRuleIds }) },
    ),
  documents: () => request<SourceDocument[]>('/api/documents'),
  registerDocument: (body: {
    title: string
    kind: string
    dms_uri: string
    version_label: string | null
    effective_from: string | null
    content_hash: string | null
  }) => request<{ document_id: string }>('/api/documents', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  /** Remove a group that never held a fund or a limit. Refused otherwise —
   *  §5.2: what a group once bound has to keep being answerable. */
  deleteScope: (scopeId: string) =>
    request<void>(`/api/scopes/${scopeId}`, { method: 'DELETE' }),
  createScope: (body: { type: string; code: string; name: string; desk_id?: string | null }) =>
    request<Scope>('/api/scopes', { method: 'POST', body: JSON.stringify(body) }),
  previewJoin: (scopeId: string, portfolioId: string) =>
    request<MembershipPreview>(
      `/api/scopes/${scopeId}/preview-join?portfolio_id=${portfolioId}`,
    ),
  addMember: (scopeId: string, portfolioId: string, shownRuleIds: string[]) =>
    request<{ attestation_due_on: string }>(`/api/scopes/${scopeId}/members`, {
      method: 'POST',
      body: JSON.stringify({ portfolio_id: portfolioId, shown_rule_ids: shownRuleIds }),
    }),

  /**
   * Which sign-in methods this appliance offers (§10.1). Unauthenticated: it is
   * read by the sign-in screen, and says nothing an anonymous caller could not
   * learn by pressing the button.
   */
  // User administration — §10.1. `admin` only; every route re-checks.
  users: () => request<User[]>('/api/users'),
  createUser: (email: string, name: string, roles: string[], deskIds: string[] = []) =>
    request<{ user_id: string; invitation_token: string }>('/api/users', {
      method: 'POST',
      body: JSON.stringify({ email, name, roles, desk_ids: deskIds }),
    }),
  /** Limit an account to some desks; none gives it the whole firm. */
  setUserDesks: (userId: string, deskIds: string[]) =>
    request<void>(`/api/users/${userId}/desks`, {
      method: 'PUT',
      body: JSON.stringify({ desk_ids: deskIds }),
    }),
  setUserRoles: (userId: string, roles: string[]) =>
    request<void>(`/api/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roles }),
    }),
  deactivateUser: (userId: string) =>
    request<void>(`/api/users/${userId}/deactivate`, { method: 'POST' }),
  // Only for an account nobody has ever claimed — see `identity.reissue_invitation`.
  reissueInvitation: (userId: string) =>
    request<{ user_id: string; invitation_token: string }>(
      `/api/users/${userId}/invitation`,
      { method: 'POST' },
    ),

  redeem: (token: string, password: string) =>
    request<Redemption>('/api/auth/invitations/redeem', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  providers: () => request<{ local: boolean; entra: boolean }>('/api/auth/providers'),

  // The mailbox connector's settings — §8.2, §8.6. `admin` only.
  feedSettings: () => request<FeedSettings | null>('/api/holdings/feed'),
  saveFeedSettings: (body: Record<string, unknown>) =>
    request<FeedSettings>('/api/holdings/feed', { method: 'PUT', body: JSON.stringify(body) }),
  // Shows the key the server presents, and the files waiting. Trusts and loads nothing.
  testFeed: () => request<FeedTest>('/api/holdings/feed/test', { method: 'POST' }),
  trustFeedKey: (fingerprint: string) =>
    request<FeedSettings>('/api/holdings/feed/trust', {
      method: 'POST',
      body: JSON.stringify({ fingerprint }),
    }),
  feedFiles: () => request<FeedFile[]>('/api/holdings/feed/files'),
  /** Marks a file to be read again; the run queued to read it, or null while loading is off. */
  retryFeedFile: (id: string) =>
    request<{ job_id: number | null }>(`/api/holdings/feed/files/${id}/retry`, { method: 'POST' }),
  connectorSettings: () => request<ConnectorSettings | null>('/api/connector/mailbox'),
  saveConnectorSettings: (body: Record<string, unknown>) =>
    request<ConnectorSettings>('/api/connector/mailbox', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  // Reads the mailbox and reports; writes nothing and marks nothing read.
  testConnector: () =>
    request<ConnectorTest>('/api/connector/mailbox/test', { method: 'POST' }),
  // A real pass: stages what it finds for an approver, and marks the messages
  // read once that has committed.
  pollConnector: () =>
    request<{ read: number; outcomes: Record<string, number>; detail: string | null }>(
      '/api/connector/mailbox/poll',
      { method: 'POST' },
    ),

  searchRules: (q: string, portfolioId?: string, includeProposed = false) => {
    const params = new URLSearchParams({ q })
    if (portfolioId) params.set('portfolio_id', portfolioId)
    if (includeProposed) params.set('include_proposed', 'true')
    return request<RuleHit[]>(`/api/search/rules?${params}`)
  },
}
